// src/features/streams/components/simulcast/YouTubeEditForm.tsx

import { Stack, TextInput, Group, Button } from '@mantine/core';
import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { SIMULCAST_CONSTRAINTS } from '@/lib/constants/simulcast';
import type { ISimulcastAllData } from '@/lib/types/Simulcast';
import type { StreamViewData } from '@/lib/types/Stream';
import { ytEditFormSchema, type YTEditFormData } from '../../schemas/SimulcastForm.schema';
import { SimulcastTransforms } from '../../simulcast/simulcastTransforms';
import { BroadcastMetadataOverrideSection } from './BroadcastMetadataOverrideSection';

interface YouTubeEditFormProps {
  simulcast: ISimulcastAllData;
  stream: StreamViewData;
  onSubmit: (data: YTEditFormData) => void;
  onCancel: () => void;
  isPending: boolean;
}

export function YouTubeEditForm({
  simulcast,
  stream,
  onSubmit,
  onCancel,
  isPending,
}: YouTubeEditFormProps) {
  const defaultValues = SimulcastTransforms.transformYTToEditFormData(simulcast);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<YTEditFormData>({
    resolver: zodResolver(ytEditFormSchema),
    defaultValues,
  });

  const { field: labelField } = useController({ name: 'label', control });

  // Show channel as read-only
  const channelLabel =
    simulcast.yt?.general?.ytChannelTitle ??
    simulcast.yt?.general?.ytOwnerDisplayName ??
    'YouTube';

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap="md">
        <TextInput
          label="Channel"
          value={channelLabel}
          readOnly
          variant="filled"
          description="Channel cannot be changed after creation"
        />

        <TextInput
          label="Destination Name"
          description="A name to identify this destination in your dashboard"
          value={labelField.value}
          onChange={labelField.onChange}
          onBlur={labelField.onBlur}
          error={errors.label?.message}
          maxLength={SIMULCAST_CONSTRAINTS.LABEL_MAX}
        />

        <BroadcastMetadataOverrideSection
          streamTitle={stream.media.title}
          streamDescription={stream.media.descShort ?? ''}
          control={control}
          errors={errors}
          existingTitle={simulcast.core.title ?? undefined}
          existingDescription={simulcast.core.descShort ?? undefined}
        />

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={isPending} disabled={!isDirty}>
            Save Changes
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
